import { apiIntegrations } from "@/api/integrations/integration.api.ts";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { emailsKeys } from "@/queries/integrations/use-emails.query.ts";
import { ApiErrorException, ApiPassException } from "@/types/api.types.ts";
import {
  ApiIntegrationEmailServersSave,
  ApiIntegrationEmployeesFilter,
  ApiIntegrationEmployeesType,
  ApiIntegrationGoogleCalendarServiceAccountSave,
  ApiIntegrationGoogleWorkspaceSave,
  ApiIntegrationSlackSave,
  ApiIntegrationType,
} from "@/types/integrations/integration.types.ts";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Control, FieldValues } from "react-hook-form";

const organizationIntegrationKeys = createQueryKeys("organizationIntegrations", {
  integrations: null,
  slackChannels: null,
  employees: (
    id: number,
    integrationType: ApiIntegrationEmployeesType,
    filter: ApiIntegrationEmployeesFilter
  ) => [id, integrationType, filter],
});

export const useIntegrations = (enabled: boolean = true) => {
  return useQuery({
    ...organizationIntegrationKeys.integrations,
    queryFn: async () => {
      const result = await apiIntegrations.getInstalledForOrganization();

      if (result.error) throw new ApiErrorException(result.error);

      return result.data;
    },
    staleTime: 60 * 1000,
    enabled,
  });
};

export const useIntegrationDisconnect = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async (id: number) => {
      const result = await apiIntegrations.uninstallForOrganization(id);

      if (result.error || !result.data) throw new ApiErrorException(result.error);

      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: organizationIntegrationKeys.integrations.queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useIntegrationAdd = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async (type: ApiIntegrationType) => {
      const result = await apiIntegrations.add(type);

      if (result.error || !result.data) throw new ApiErrorException(result.error);

      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: organizationIntegrationKeys.integrations.queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useIntegrationGoogleCalendarServiceAccount = (
  id: number | undefined,
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: ["integrationGoogleCalendarServiceAccount", id],
    queryFn: async () => {
      if (id == undefined) throw new ApiPassException("Employee id is required");
      const result = await apiIntegrations.getIntegrationGoogleServiceAccount(id);

      if (result.error) throw new ApiErrorException(result.error);

      return result.data;
    },
    enabled: enabled && !!id,
  });
};

export const useIntegrationGoogleCalendarServiceAccountUpdate = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async ({
      id,
      payload,
    }: {
      id: number;
      payload: ApiIntegrationGoogleCalendarServiceAccountSave;
    }) => {
      const result = await apiIntegrations.updateIntegrationGoogleServiceAccount(id, payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);

      return result.data;
    },

    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({
        queryKey: organizationIntegrationKeys.integrations.queryKey,
      });
      queryClient.invalidateQueries({ queryKey: ["integrationGoogleCalendarServiceAccount", id] });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useIntegrationSlack = (id: number | undefined, enabled: boolean = true) => {
  return useQuery({
    queryKey: ["integrationSlack", id],
    queryFn: async () => {
      if (id == undefined) throw new ApiPassException("Idd is required");
      const result = await apiIntegrations.getIntegrationSlack(id);

      if (result.error) throw new ApiErrorException(result.error);

      return result.data;
    },
    enabled: enabled && !!id,
  });
};

export const useIntegrationGoogleWorkspace = (id: number | undefined, enabled: boolean = true) => {
  return useQuery({
    queryKey: ["integrationGoogleWorkspace", id],
    queryFn: async () => {
      if (id == undefined) throw new ApiPassException("Idd is required");
      const result = await apiIntegrations.getIntegrationGoogleWorkspace(id);

      if (result.error) throw new ApiErrorException(result.error);

      return result.data;
    },
    enabled: enabled && !!id,
  });
};

export const useIntegrationSlackUpdate = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async ({ id, payload }: { id: number; payload: ApiIntegrationSlackSave }) => {
      const result = await apiIntegrations.updateIntegrationSlack(id, payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);

      return result.data;
    },

    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({
        queryKey: organizationIntegrationKeys.integrations.queryKey,
      });
      queryClient.invalidateQueries({ queryKey: ["integrationSlack", id] });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useIntegrationEmailServersUpdate = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async ({
      id,
      payload,
    }: {
      id?: number;
      payload: ApiIntegrationEmailServersSave;
    }) => {
      const result = id
        ? await apiIntegrations.updateIntegrationEmailServers(id, payload)
        : await apiIntegrations.addIntegrationEmailServers(payload);

      if (result.error || !result.data) throw new ApiErrorException(result.error);

      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: organizationIntegrationKeys.integrations.queryKey,
      });

      queryClient.invalidateQueries({
        queryKey: emailsKeys.integrations.queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useIntegrationEmailServers = (id: number | undefined, enabled: boolean = true) => {
  return useQuery({
    queryKey: ["integrationEmailServers", id],
    queryFn: async () => {
      if (id == undefined) throw new ApiPassException("Id is required");
      const result = await apiIntegrations.getIntegrationEmailServers(id);

      if (result.error) throw new ApiErrorException(result.error);

      return result.data;
    },
    enabled: enabled && !!id,
  });
};

export const useSlackChannels = (enabled: boolean = true) => {
  return useQuery({
    ...organizationIntegrationKeys.slackChannels,
    queryFn: async () => {
      const result = await apiIntegrations.getSlackChannels();
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
    enabled,
  });
};

export const useIntegrationGoogleWorkspaceUpdate = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async ({
      id,
      payload,
    }: {
      id: number;
      payload: ApiIntegrationGoogleWorkspaceSave;
    }) => {
      const result = await apiIntegrations.updateIntegrationGoogleWorkspace(id, payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);

      return result.data;
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: organizationIntegrationKeys.integrations.queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useIntegrationEmployees = <TType extends ApiIntegrationEmployeesType>(
  id: number,
  integrationType: TType,
  filter: ApiIntegrationEmployeesFilter,
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: organizationIntegrationKeys.employees(id, integrationType, filter).queryKey,
    queryFn: async () => {
      const result = await apiIntegrations.searchEmployees(id, integrationType, filter);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    enabled,
  });
};

export const useIntegrationEmployeesSync = () => {
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async ({
      id,
      integrationType,
    }: {
      id: number;
      integrationType: ApiIntegrationEmployeesType;
    }) => {
      const result = await apiIntegrations.syncEmployees(id, integrationType);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};
